<template>
  <div class="document-notes-tab-container">
    <div class="position-absolute w-100 h-100">
      <div
        ref="comments"
        class="w-100 overflow-auto pl-2"
        style="height: calc(100% - 145px)"
      >
        <template v-if="notes.length">
          <div v-for="[key, notes] in groupedNotes" :key="key">
            <div class="text-center mb-3" style="color: #bbb">
              <small> {{ key }} </small>
            </div>
            <user-comment
              v-for="note in notes"
              v-on:update:mentions="e => updateMention(note, e)"
              :key="note.uniqueId"
              :user="note.user"
              :comment="note.note"
              :mentions="note.mentions"
              :date="note.time"
              :actions="note.actions"
              :editMode="note.editMode"
              class="mb-3"
              @update="e => updateNote(note, e)"
              @cancel="_ => calcelNoteEdition(note.uniqueId)"
            />
          </div>
        </template>
        <template v-else>
          <span style="color: #b1b1b1;font-size: 14px;">
            No notes yet.
          </span>
        </template>
      </div>
      <smart-text-input
        v-model="currentNote"
        :readonly="readonly"
        :mentions.sync="mentions"
        :maxLength="5000"
        :showListOptions="false"
        backgroundColor="#FFF"
        class="position-absolute"
        style="bottom: 21px; width: calc(100% + 10px); margin-left: -5px !important"
        @submit="addNote"
      />
      <div
        class="position-absolute"
        style="font-size:10.5px; bottom: 0px; font-style:italic;"
      >
        <i class="fa fa-info-circle text-primary" /> Notes will NOT be included
        on the document or the patient chart.
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import documentNotesServer from "../../../server/documentNotes";
import notificationServer from "../../../server/notification";
import { groupBy, randomId } from "../../../helpers/general";
import { formatRelativeDatePretty } from "../../../helpers/date";
import SmartTextInput from "../../controls/SmartTextInput.vue";
import UserComment from "../../../views/UserComment.vue";

export default {
  name: "DocumentNotesTabContent",
  components: { SmartTextInput, UserComment },
  props: {
    documentId: {
      type: String
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      notes: [],
      currentNote: null,
      mentions: []
    };
  },
  mounted() {
    this.getNotes(this.documentId);
  },
  methods: {
    async getNotes(documentId) {
      const notes = documentId
        ? await documentNotesServer.getDocumentNotes(documentId)
        : [];

      this.notes = notes.map(n => ({
        ...n,
        uniqueId: randomId(),
        mentions: []
      }));

      this.scrollBottom();
    },
    clearNotes() {
      this.notes = [];
    },
    mapNote(note, index) {
      const today = this.getCurrentAdjustedDateTime();
      return {
        id: note.id,
        date: formatRelativeDatePretty(today, note.createdAt),
        time: note.createdAt ? this.formatTimeOnly(note.createdAt) : "",
        user: note.createdBy,
        note: note.note,
        editMode: note.editMode ?? false,
        uniqueId: note.uniqueId,
        mentions: note.mentions,
        index
      };
    },
    addNote(e) {
      const note = { note: e, documentId: this.documentId };
      if (this.documentId) {
        documentNotesServer.create(note).then(response => {
          response.uniqueId = randomId();
          this.notes.push(response);
          if (this.mentions.length) {
            notificationServer.create({
              eventName: "TaggedInDocumentNote",
              userIds: this.mentions,
              primaryEntityId: this.documentId
            });
          }
          this.scrollBottom();
          this.$emit("onChange");
        });
      } else {
        this.notes.push({ ...note, mentions: this.mentions });
        this.scrollBottom();
      }

      this.currentNote = "";
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.comments;
        el.scrollTop = el.scrollHeight;
      });
    },
    updateNote(note, e) {
      const uniqueId = randomId();
      note = { ...note, note: e, uniqueId, editMode: false };
      this.notes.splice(note.index, 1, note);

      if (note.id) {
        documentNotesServer.update(note.id, note).then(response => {
          response.uniqueId = uniqueId;
          this.notes.splice(note.index, 1, response);
          if (note.mentions.length) {
            notificationServer.create({
              eventName: "TaggedInDocumentNote",
              userIds: note.mentions,
              primaryEntityId: this.documentId
            });
          }
        });
      } else {
        this.notes.splice(note.index, 1, note);
      }
    },
    updateMention(note, e) {
      note = { ...note, mentions: e };
      this.notes.splice(note.index, 1, note);
    },
    deleteNote(note) {
      if (note.id) {
        documentNotesServer.delete(note.id);
        this.$emit("onChange");
      }
      this.notes.splice(note.index, 1);
    },
    async createBatch(documentId) {
      if (documentId) {
        const notes = this.notes.filter(u => u.id == null);
        if (notes.length) {
          const batch = notes.map(u => ({ ...u, documentId }));
          await documentNotesServer.createBatch(batch);
          notes.forEach(async n => {
            await notificationServer.create({
              eventName: "TaggedInDocumentNote",
              userIds: n.mentions || [],
              primaryEntityId: documentId
            });
          });
          this.$emit("onChange");
        }
      }
    },
    calcelNoteEdition(uniqueId) {
      const index = this.notes.findIndex(n => n.uniqueId == uniqueId);
      const note = this.notes[index];
      const newNote = { ...note, editMode: false };
      this.notes.splice(index, 1, newNote);
    }
  },
  computed: {
    ...mapState("layout", {
      currentUser: state => state.currentUser
    }),
    ...mapGetters("layout", [
      "getCurrentAdjustedDateTime",
      "hasCurrentUserPermissionAccess"
    ]),
    groupedNotes() {
      const notes = this.notes.map((n, i) => {
        const note = this.mapNote(n, i);
        note.actions = [];

        if (
          !note.user ||
          this.currentUser.id == note.user.id ||
          this.hasCurrentUserPermissionAccess("patients", "editAllNotes")
        ) {
          note.actions.push({
            content: "Edit",
            callback: () => {
              const newNote = { ...n, editMode: true };
              this.notes.splice(i, 1, newNote);
            }
          });
          note.actions.push({
            content: "Delete",
            callback: () => {
              this.$dialog
                .alert(
                  { title: "Are you sure you want to delete this record?" },
                  { view: "confirm-delete" }
                )
                .then(() => {
                  this.deleteNote(note);
                });
            }
          });
        }

        return note;
      });

      return Array.from(groupBy(notes, n => n.date));
    }
  },
  watch: {
    async documentId(newValue) {
      const isAllNotesNew =
        this.notes.length > 0 && this.notes.every(note => !note.id);

      if (!isAllNotesNew) await this.getNotes(newValue);
    }
  }
};
</script>

<style scoped>
.document-notes-tab-container {
  position: relative;
  flex-grow: 1;
  width: 100%;
  height: 100%;
}
</style>
